import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Check,
  ChevronRight,
  Cpu,
  Loader2,
  Lock,
  Mail,
  MonitorCheck,
  PencilLine,
  ShieldAlert,
  X,
  Zap,
} from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useAuthStore } from "@/store/auth";
import { formatDateTime } from "@/utils/formatDate";
import { internalUsersApi } from "@/features/tasks/api/users";
import { useAronWorkModes } from "../api/aronApi";
import { ModeCard } from "./AronModelPicker";
import {
  useCreateAronScheduledTask,
  usePreviewAronSchedule,
  useUpdateAronScheduledTask,
} from "../api/aronTasksApi";
import {
  DEFAULT_PLAN,
  WEEKDAY_LABELS,
  WEEKDAY_TOKENS,
  cronToPlan,
  planLabel,
  planToCron,
  type SchedulePlan,
  type SchedulePresetKind,
} from "../utils/cronPlan";
import type {
  AronAudienceSelector,
  AronPlannerOptions,
  AronReasoningMode,
  AronSchedulePreview,
  AronScheduledTask,
  AronScheduledTaskInput,
  AronScheduledTaskRunMode,
} from "../types";

/**
 * Create/edit a scheduled task straight from the planner.
 *
 * The form never decides anything the runtime will decide again: the schedule is
 * previewed by the SERVER's cron parser, and how much the task may DO is the same
 * three-level choice a human makes for a chat — read / propose / act — applied to
 * the run conversation rather than to a hand-written endpoint list.
 *
 * Delivery defaults to THIS page: no e-mail unless asked for.
 */

const LABEL =
  "font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-[var(--color-text-secondary)]";
const FIELD =
  "w-full border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-1.5 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-primary)] focus:outline-none";
const CHIP =
  "border px-2 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors";

const PRESETS: { kind: SchedulePresetKind; label: string }[] = [
  { kind: "hourly", label: "Co N godzin" },
  { kind: "daily", label: "Codziennie" },
  { kind: "weekdays", label: "Dni robocze" },
  { kind: "weekly", label: "Wybrane dni" },
  { kind: "monthly", label: "Co miesiąc" },
  { kind: "custom", label: "Własny cron" },
];

const RUN_MODE_ICONS: Record<AronScheduledTaskRunMode, typeof Lock> = {
  read_only: Lock,
  proposals: PencilLine,
  autopilot: Zap,
};

/** Fallback when planner-options has not loaded yet — same wording as the server. */
const RUN_MODE_FALLBACK: {
  value: AronScheduledTaskRunMode;
  label: string;
  detail: string;
}[] = [
  {
    value: "read_only",
    label: "Tylko odczyt",
    detail: "ARON czyta dane i pisze raport. Nic nie zapisuje.",
  },
  {
    value: "proposals",
    label: "Propozycje",
    detail: "ARON przygotowuje zapisy, ale każdy czeka na Twoje zatwierdzenie.",
  },
  {
    value: "autopilot",
    label: "Tryb samodzielny",
    detail: "ARON wykonuje zapisy od razu, z Twoimi uprawnieniami.",
  },
];

function parseAddresses(raw: string): string[] {
  return raw
    .split(/[,;\n]/)
    .map((p) => p.trim())
    .filter(Boolean);
}

function errorText(err: unknown, fallback: string): string {
  const response = (err as { response?: { data?: { errors?: unknown; detail?: unknown } } })
    ?.response;
  const payload = response?.data?.errors ?? response?.data?.detail;
  if (!payload) return fallback;
  if (typeof payload === "string") return payload;
  if (Array.isArray(payload)) return payload.map(String).join(" ");
  if (typeof payload === "object") {
    return Object.values(payload as Record<string, unknown>)
      .flatMap((v) => (Array.isArray(v) ? v.map(String) : [String(v)]))
      .join(" ");
  }
  return fallback;
}

function fmtRun(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString("pl-PL", { weekday: "short" })} ${formatDateTime(d)}`;
}

function Section({
  index,
  title,
  hint,
  children,
}: {
  index: number;
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="border border-[var(--color-border)]">
      <header className="flex items-center gap-2 border-b border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-1.5">
        <span className="flex h-4 w-4 items-center justify-center bg-[var(--color-primary)] font-mono text-[10px] font-bold text-white">
          {index}
        </span>
        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--color-text-primary)]">
          {title}
        </span>
        {hint && (
          <span className="ml-auto font-mono text-[10px] text-[var(--color-text-muted)]">
            {hint}
          </span>
        )}
      </header>
      <div className="space-y-3 p-3">{children}</div>
    </section>
  );
}

interface Props {
  open: boolean;
  /** null → a brand-new task. */
  task: AronScheduledTask | null;
  options: AronPlannerOptions | undefined;
  onClose: () => void;
}

export function AronTaskEditor({ open, task, options, onClose }: Props) {
  const user = useAuthStore((s) => s.user);
  const { data: workModes } = useAronWorkModes();
  const create = useCreateAronScheduledTask();
  const update = useUpdateAronScheduledTask();
  const preview = usePreviewAronSchedule();

  const [name, setName] = useState("");
  const [instruction, setInstruction] = useState("");
  const [plan, setPlan] = useState<SchedulePlan>(DEFAULT_PLAN);
  const [mode, setMode] = useState<AronReasoningMode>("standard");
  const [runMode, setRunMode] = useState<AronScheduledTaskRunMode>("proposals");
  const [byMail, setByMail] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [visibility, setVisibility] = useState<"personal" | "shared">("personal");
  const [audienceSel, setAudienceSel] = useState<AronAudienceSelector[]>([]);
  const [previewData, setPreviewData] = useState<AronSchedulePreview | null>(null);
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  const [error, setError] = useState("");

  const editing = task !== null;
  const cron = useMemo(() => planToCron(plan), [plan]);

  // Seed when the modal opens (and only then — a background refetch of the list
  // must never overwrite what is being typed).
  useEffect(() => {
    if (!open) return;
    setError("");
    setPreviewData(null);
    setModelPickerOpen(false);
    if (task) {
      setName(task.name);
      setInstruction(task.instruction);
      setPlan(cronToPlan(task.cron_expression));
      setMode(task.reasoning_mode);
      setRunMode(task.run_mode);
      setByMail(task.email_to.length > 0);
      setEmailTo(task.email_to.join(", "));
      setEmailCc(task.email_cc.join(", "));
      setVisibility(task.visibility);
      setAudienceSel(task.shared_audience);
    } else {
      setName("");
      setInstruction("");
      setPlan(DEFAULT_PLAN);
      setMode("standard");
      setRunMode("proposals");
      setByMail(false);
      setEmailTo(user?.email ?? "");
      setEmailCc("");
      setVisibility("personal");
      setAudienceSel([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task?.id]);

  // Debounced schedule preview — the server answers what the expression means and
  // when it fires, including the "not more often than X" floor.
  const previewRef = useRef(preview.mutate);
  previewRef.current = preview.mutate;
  useEffect(() => {
    if (!open || !cron.trim()) return;
    const handle = window.setTimeout(() => {
      previewRef.current(cron, { onSuccess: (data) => setPreviewData(data) });
    }, 350);
    return () => window.clearTimeout(handle);
  }, [cron, open]);

  const modes = workModes?.reasoning_modes ?? [];
  const activeMode = modes.find((m) => m.value === mode);
  const runModes = options?.run_modes?.length ? options.run_modes : RUN_MODE_FALLBACK;
  const canAutopilot = !!options?.can_autopilot;
  const saving = create.isPending || update.isPending;

  const submit = () => {
    setError("");
    const payload: AronScheduledTaskInput = {
      name: name.trim(),
      instruction: instruction.trim(),
      cron_expression: cron,
      reasoning_mode: mode,
      run_mode: runMode,
      // Empty = report stays on this page. write_scope is deliberately NOT sent:
      // it is defined in chat, and an edit here must not silently drop it.
      email_to: byMail ? parseAddresses(emailTo) : [],
      email_cc: byMail ? parseAddresses(emailCc) : [],
      visibility,
      shared_audience: visibility === "shared" ? audienceSel : [],
    };
    const onError = (err: unknown) =>
      setError(errorText(err, "Nie udało się zapisać zadania."));
    if (editing && task) {
      update.mutate({ id: task.id, patch: payload }, { onSuccess: onClose, onError });
    } else {
      create.mutate(payload, { onSuccess: onClose, onError });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={editing ? `Edytuj: ${task?.name ?? ""}` : "Nowe zadanie cykliczne"}
    >
      <div className="space-y-3">
        {options && !options.scheduled_tasks_enabled && (
          <p className="flex items-start gap-2 border border-[var(--color-primary)] bg-[var(--color-primary-dim)] px-2 py-1.5 text-[11px] text-[var(--color-text-primary)]">
            <AlertTriangle size={13} className="mt-0.5 shrink-0 text-[var(--color-primary)]" />
            Automat wykonujący zadania cykliczne jest wyłączony — zadanie zapiszesz, ale nie
            uruchomi się samo. Możesz je odpalać ręcznie przyciskiem „Uruchom teraz”.
          </p>
        )}

        <Section index={1} title="Zadanie">
          <div>
            <label className={LABEL} htmlFor="task-name">
              Nazwa
            </label>
            <input
              id="task-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="np. Raport tygodniowy PRZ"
              className={`${FIELD} mt-1`}
            />
          </div>
          <div>
            <label className={LABEL} htmlFor="task-instruction">
              Co ARON ma zrobić
            </label>
            <textarea
              id="task-instruction"
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              rows={5}
              placeholder="Sprawdź faktury PRZ z ostatniego tygodnia, zestaw te bez POCK i opisz, co blokuje rozliczenie."
              className={`${FIELD} mt-1 font-mono text-[12px] leading-relaxed`}
            />
            <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
              Ta treść trafia do ARONa przy każdym przebiegu. Pisz jak do kogoś, kto nie może
              dopytać — jego końcowa odpowiedź jest wynikiem zadania.
            </p>
          </div>
        </Section>

        <Section
          index={2}
          title="Harmonogram"
          hint={
            options?.min_interval_minutes
              ? `nie częściej niż co ${options.min_interval_minutes} min`
              : undefined
          }
        >
          <ScheduleFields
            plan={plan}
            onChange={setPlan}
            cron={cron}
            preview={previewData}
            isPreviewing={preview.isPending}
          />
        </Section>

        <Section index={3} title="Jak ARON ma pracować">
          <div className="grid gap-2 sm:grid-cols-3">
            {runModes.map((m) => {
              const Icon = RUN_MODE_ICONS[m.value] ?? Lock;
              const locked = m.value === "autopilot" && !canAutopilot;
              const active = runMode === m.value;
              return (
                <button
                  key={m.value}
                  type="button"
                  disabled={locked}
                  onClick={() => setRunMode(m.value)}
                  title={locked ? "Wymaga uprawnienia do Trybu samodzielnego" : undefined}
                  className={`relative flex flex-col gap-1 border p-2.5 pl-3 text-left transition-colors ${
                    active
                      ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)]"
                      : "border-[var(--color-border)] hover:border-[var(--color-text-primary)]"
                  } ${locked ? "cursor-not-allowed opacity-45" : ""}`}
                >
                  {active && (
                    <span className="absolute top-0 bottom-0 left-0 w-[3px] bg-[var(--color-primary)]" />
                  )}
                  <span className="flex items-center gap-1.5">
                    <Icon
                      size={13}
                      className={
                        active
                          ? "text-[var(--color-primary)]"
                          : "text-[var(--color-text-muted)]"
                      }
                    />
                    <span
                      className={`font-mono text-[10px] font-bold uppercase tracking-wider ${
                        active
                          ? "text-[var(--color-primary)]"
                          : "text-[var(--color-text-primary)]"
                      }`}
                    >
                      {m.label}
                    </span>
                  </span>
                  <span className="text-[11px] leading-snug text-[var(--color-text-secondary)]">
                    {m.detail}
                  </span>
                </button>
              );
            })}
          </div>

          {runMode === "autopilot" && (
            <p className="flex items-start gap-1.5 border border-[var(--color-primary)] bg-[var(--color-primary-dim)] px-2 py-1.5 text-[11px] text-[var(--color-text-primary)]">
              <ShieldAlert size={13} className="mt-0.5 shrink-0 text-[var(--color-primary)]" />
              W tym trybie zapisy wykonują się bez pytania, przy każdym przebiegu — z Twoimi
              uprawnieniami i pod Twoim nazwiskiem. Każdy zapis jest wypisany w wyniku.
            </p>
          )}

          {editing && (task?.write_scope.length ?? 0) > 0 && (
            <p className="text-[11px] text-[var(--color-text-muted)]">
              To zadanie ma dodatkowo zakres zapisów ustalony w rozmowie z ARONem (
              {task?.write_scope.length}) — zostaje bez zmian.
            </p>
          )}

          <div>
            <span className={LABEL}>Model / tryb myślenia</span>
            <button
              type="button"
              onClick={() => setModelPickerOpen(true)}
              className="mt-1 flex w-full items-center gap-2 border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-2 text-left transition-colors hover:border-[var(--color-primary)]"
            >
              <Cpu size={14} className="shrink-0 text-[var(--color-primary)]" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-[var(--color-text-primary)]">
                  {activeMode ? activeMode.label : task?.mode_info.label ?? mode}
                </span>
                <span className="block truncate font-mono text-[11px] text-[var(--color-text-secondary)]">
                  {activeMode
                    ? `${activeMode.model} · ${activeMode.detail}`
                    : task?.mode_info.model ?? ""}
                </span>
              </span>
              <span className="border border-[var(--color-border)] px-1 font-mono text-[10px] text-[var(--color-text-secondary)]">
                {activeMode?.cost ?? task?.mode_info.cost ?? ""}
              </span>
              <ChevronRight size={14} className="shrink-0 text-[var(--color-text-muted)]" />
            </button>
            <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
              Zmiana obowiązuje od następnego przebiegu.
            </p>
          </div>
        </Section>

        <Section index={4} title="Wynik">
          <div className="grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setByMail(false)}
              className={`relative flex flex-col gap-1 border p-2.5 pl-3 text-left transition-colors ${
                byMail
                  ? "border-[var(--color-border)] hover:border-[var(--color-text-primary)]"
                  : "border-[var(--color-primary)] bg-[var(--color-primary-dim)]"
              }`}
            >
              {!byMail && (
                <span className="absolute top-0 bottom-0 left-0 w-[3px] bg-[var(--color-primary)]" />
              )}
              <span className="flex items-center gap-1.5">
                <MonitorCheck
                  size={13}
                  className={byMail ? "text-[var(--color-text-muted)]" : "text-[var(--color-primary)]"}
                />
                <span
                  className={`font-mono text-[10px] font-bold uppercase tracking-wider ${
                    byMail ? "text-[var(--color-text-primary)]" : "text-[var(--color-primary)]"
                  }`}
                >
                  Tylko na tej stronie
                </span>
              </span>
              <span className="text-[11px] leading-snug text-[var(--color-text-secondary)]">
                Wynik czeka na karcie zadania — świeży oznaczony plakietką „nowy wynik”.
              </span>
            </button>

            <button
              type="button"
              onClick={() => setByMail(true)}
              className={`relative flex flex-col gap-1 border p-2.5 pl-3 text-left transition-colors ${
                byMail
                  ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)]"
                  : "border-[var(--color-border)] hover:border-[var(--color-text-primary)]"
              }`}
            >
              {byMail && (
                <span className="absolute top-0 bottom-0 left-0 w-[3px] bg-[var(--color-primary)]" />
              )}
              <span className="flex items-center gap-1.5">
                <Mail
                  size={13}
                  className={byMail ? "text-[var(--color-primary)]" : "text-[var(--color-text-muted)]"}
                />
                <span
                  className={`font-mono text-[10px] font-bold uppercase tracking-wider ${
                    byMail ? "text-[var(--color-primary)]" : "text-[var(--color-text-primary)]"
                  }`}
                >
                  Dodatkowo e-mailem
                </span>
              </span>
              <span className="text-[11px] leading-snug text-[var(--color-text-secondary)]">
                Raport idzie mailem na wskazane adresy (tylko wewnętrzne).
              </span>
            </button>
          </div>

          {byMail && (
            <div className="grid gap-2 sm:grid-cols-2">
              <input
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="imie.nazwisko@elektim.pl"
                className={FIELD}
              />
              <input
                value={emailCc}
                onChange={(e) => setEmailCc(e.target.value)}
                placeholder="DW (opcjonalnie)"
                className={FIELD}
              />
            </div>
          )}

          <AudienceFields
            options={options}
            visibility={visibility}
            onVisibilityChange={setVisibility}
            audience={audienceSel}
            onAudienceChange={setAudienceSel}
          />
        </Section>

        {error && (
          <p className="border border-[var(--color-primary)] bg-[var(--color-primary-dim)] px-2 py-1.5 text-[12px] text-[var(--color-text-primary)]">
            {error}
          </p>
        )}

        {modelPickerOpen && (
          <div
            className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/50 p-4"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setModelPickerOpen(false);
            }}
          >
            <div className="w-full max-w-3xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
              <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
                <span className="font-mono text-sm font-bold uppercase tracking-wider text-[var(--color-text-primary)]">
                  Model dla tego zadania
                </span>
                <button
                  type="button"
                  onClick={() => setModelPickerOpen(false)}
                  className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="px-4 py-3">
                <p className="mb-3 border-b border-[var(--color-border)] pb-3 text-xs text-[var(--color-text-secondary)]">
                  Model obowiązuje od <b>następnego</b> przebiegu — wcześniejsze wyniki
                  zostają przy modelu, którym powstały. Ceny dostawcy w USD za 1 mln tokenów
                  (in = wejście / out = wyjście); gwiazdki to nasza ocena jakości na trudnych
                  zadaniach.
                </p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {modes.map((m) => (
                    <ModeCard
                      key={m.value}
                      mode={m}
                      active={m.value === mode}
                      onPick={() => {
                        setMode(m.value as AronReasoningMode);
                        setModelPickerOpen(false);
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 border-t border-[var(--color-border)] pt-3">
          <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
            {previewData?.valid ? previewData.human : planLabel(plan)}
          </span>
          <span className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="border border-[var(--color-border)] px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-[var(--color-text-secondary)] hover:border-[var(--color-text-primary)] hover:text-[var(--color-text-primary)]"
            >
              Anuluj
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="flex items-center gap-1.5 bg-[var(--color-primary)] px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-wider text-white hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
              {editing ? "Zapisz zmiany" : "Utwórz zadanie"}
            </button>
          </span>
        </div>
      </div>
    </Modal>
  );
}

function ScheduleFields({
  plan,
  onChange,
  cron,
  preview,
  isPreviewing,
}: {
  plan: SchedulePlan;
  onChange: (p: SchedulePlan) => void;
  cron: string;
  preview: AronSchedulePreview | null;
  isPreviewing: boolean;
}) {
  const set = (patch: Partial<SchedulePlan>) => onChange({ ...plan, ...patch });
  const timeValue = `${String(plan.hour).padStart(2, "0")}:${String(plan.minute).padStart(2, "0")}`;

  return (
    <>
      <div className="flex flex-wrap gap-1">
        {PRESETS.map((p) => (
          <button
            key={p.kind}
            type="button"
            onClick={() =>
              set({ kind: p.kind, cron: p.kind === "custom" ? cron : plan.cron })
            }
            className={`${CHIP} ${
              plan.kind === p.kind
                ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        {plan.kind === "hourly" ? (
          <label className="flex flex-col gap-1">
            <span className={LABEL}>Co ile godzin</span>
            <input
              type="number"
              min={1}
              max={23}
              value={plan.everyHours}
              onChange={(e) => set({ everyHours: Number(e.target.value) })}
              className={`${FIELD} w-24`}
            />
          </label>
        ) : plan.kind === "custom" ? (
          <label className="flex flex-1 flex-col gap-1">
            <span className={LABEL}>Wyrażenie cron (5 pól)</span>
            <input
              value={plan.cron}
              onChange={(e) => set({ cron: e.target.value })}
              placeholder="0 16 * * fri"
              className={`${FIELD} font-mono`}
            />
          </label>
        ) : (
          <label className="flex flex-col gap-1">
            <span className={LABEL}>Godzina</span>
            <input
              type="time"
              value={timeValue}
              onChange={(e) => {
                const [h, m] = e.target.value.split(":").map(Number);
                set({ hour: h || 0, minute: m || 0 });
              }}
              className={`${FIELD} w-32`}
            />
          </label>
        )}

        {plan.kind === "monthly" && (
          <label className="flex flex-col gap-1">
            <span className={LABEL}>Dzień miesiąca</span>
            <input
              type="number"
              min={1}
              max={28}
              value={plan.dayOfMonth}
              onChange={(e) => set({ dayOfMonth: Number(e.target.value) })}
              className={`${FIELD} w-24`}
            />
          </label>
        )}

        {plan.kind === "weekly" && (
          <div className="flex flex-col gap-1">
            <span className={LABEL}>Dni tygodnia</span>
            <div className="flex flex-wrap gap-1">
              {WEEKDAY_TOKENS.map((d) => {
                const on = plan.weekdays.includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() =>
                      set({
                        weekdays: on
                          ? plan.weekdays.filter((x) => x !== d)
                          : [...plan.weekdays, d],
                      })
                    }
                    className={`${CHIP} ${
                      on
                        ? "border-[var(--color-text-primary)] bg-[var(--color-text-primary)] text-[var(--color-text-inverse)]"
                        : "border-[var(--color-border)] text-[var(--color-text-secondary)]"
                    }`}
                  >
                    {WEEKDAY_LABELS[d]}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-[var(--color-border-muted)] pt-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="border border-[var(--color-border)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-text-muted)]">
            {cron || "—"}
          </span>
          <span className="text-[12px] font-medium text-[var(--color-text-primary)]">
            {preview?.valid ? preview.human : planLabel(plan)}
          </span>
          {isPreviewing && (
            <Loader2 size={11} className="animate-spin text-[var(--color-text-muted)]" />
          )}
        </div>
        {preview && !preview.valid && (
          <p className="mt-1 flex items-start gap-1.5 text-[11px] text-[var(--color-primary)]">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            {preview.error}
          </p>
        )}
        {preview?.valid && preview.next_runs.length > 0 && (
          <p className="mt-1 font-mono text-[10px] text-[var(--color-text-muted)]">
            NAJBLIŻSZE: {preview.next_runs.slice(0, 4).map(fmtRun).join(" · ")}
          </p>
        )}
      </div>
    </>
  );
}

function AudienceFields({
  options,
  visibility,
  onVisibilityChange,
  audience,
  onAudienceChange,
}: {
  options: AronPlannerOptions | undefined;
  visibility: "personal" | "shared";
  onVisibilityChange: (v: "personal" | "shared") => void;
  audience: AronAudienceSelector[];
  onAudienceChange: (a: AronAudienceSelector[]) => void;
}) {
  const canShare = !!options?.can_share;
  const { data: users } = useQuery({
    queryKey: ["internal-users", "aron-planner"],
    queryFn: () => internalUsersApi.list(),
    enabled: canShare && visibility === "shared",
    staleTime: 5 * 60_000,
  });

  const has = (type: string) => audience.some((a) => a.type === type && !a.id);
  const toggle = (type: string) =>
    onAudienceChange(
      has(type)
        ? audience.filter((a) => !(a.type === type && !a.id))
        : [...audience, { type: type as AronAudienceSelector["type"] }],
    );

  const userOptions = (users ?? []).map((u) => ({
    value: String(u.id),
    label: u.full_name || [u.first_name, u.last_name].filter(Boolean).join(" ") || u.username,
  }));

  // Nothing to choose for someone who cannot share — the task is simply personal.
  if (!canShare && visibility !== "shared") return null;

  return (
    <div className="border-t border-[var(--color-border-muted)] pt-3">
      <span className={LABEL}>Kto widzi wynik</span>
      <div className="mt-1.5 flex flex-wrap gap-1">
        {(["personal", "shared"] as const).map((v) => (
          <button
            key={v}
            type="button"
            disabled={v === "shared" && !canShare}
            onClick={() => onVisibilityChange(v)}
            className={`${CHIP} ${
              visibility === v
                ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                : "border-[var(--color-border)] text-[var(--color-text-secondary)] disabled:opacity-40"
            }`}
          >
            {v === "personal" ? "Tylko ja" : "Współdzielone"}
          </button>
        ))}
      </div>

      {visibility === "shared" && (
        <div className="mt-2 space-y-2">
          <div className="flex flex-wrap gap-1">
            {(options?.audience_options ?? [])
              .filter((o) => o.type !== "user")
              .map((o) => (
                <button
                  key={o.type}
                  type="button"
                  onClick={() => toggle(o.type)}
                  className={`${CHIP} ${
                    has(o.type)
                      ? "border-[var(--color-text-primary)] bg-[var(--color-text-primary)] text-[var(--color-text-inverse)]"
                      : "border-[var(--color-border)] text-[var(--color-text-secondary)]"
                  }`}
                >
                  {o.label}
                </button>
              ))}
          </div>

          <div className="flex flex-wrap items-center gap-1">
            {audience
              .filter((a) => a.type === "user" && a.id)
              .map((a) => (
                <span
                  key={a.id}
                  className="flex items-center gap-1 border border-[var(--color-border)] px-1.5 py-0.5 text-[11px]"
                >
                  {userOptions.find((u) => u.value === a.id)?.label ?? a.id}
                  <button
                    type="button"
                    onClick={() => onAudienceChange(audience.filter((x) => x.id !== a.id))}
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                  >
                    ×
                  </button>
                </span>
              ))}
          </div>

          <SearchableSelect
            options={userOptions}
            placeholder="Dodaj osobę…"
            value=""
            onChange={(e) => {
              const id = e.target.value;
              if (!id || audience.some((a) => a.id === id)) return;
              onAudienceChange([...audience, { type: "user", id }]);
            }}
          />
          <p className="text-[11px] text-[var(--color-text-muted)]">
            Zadanie współdzielone zdefiniowane przez kogoś innego niż CEO czeka na jego
            akceptację, zanim pierwszy raz się uruchomi.
          </p>
        </div>
      )}
    </div>
  );
}
